import { normalizeTeamData } from '@sportal365/smp-widget-normalizers';
import { camelToSnakeCase, defaultHandleTeamClick, snakeToCamelCase, toSentenceCase } from '@sportal365/smp-widget-utils';
import {
  ComparisonRow,
  ConfigContext,
  OddsDisplay,
  ProfileHead,
  SMPFlex,
  SMPLoader,
  SMPStatsWrapper,
  SMPText,
  SubHeader,
} from '@velinsi/smp-widgets-ui';
import { useState } from 'react';
import { GenericAlign, GenericSize } from '../../../../../packages/constants/generic';
import { Sports } from '../../../../../packages/constants/sportType';
import useResizeObserver from '../../../../../packages/hooks/useResizeObserver';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { styled } from '../../../../../packages/theme/stitches.config';
import { BaseWidgetProps, IFootballTeamMatchH2hWidgetConfig, ITeamStatistics } from '../../../../../packages/types';
import useReconcileScopeMarkets from '../../hooks/useReconcileScopeMarkets';
import useTeam from '../../hooks/useTeam';

const LAYOUT_SIZE_CHANGE = 740;

const MATCH_STATS = [
  'corners',
  'counterAttacks',
  'crosses',
  'foulsCommitted',
  'goalKicks',
  'goals',
  'offside',
  'possession',
  'redCards',
  'yellowCards',
  'shotsBlocked',
  'shotsOff',
  'shotsOn',
  'substitutions',
  'throwIn',
  'treatments',
];

const NEGATIVE_STATS = ['foulsCommitted', 'goalKicks', 'offside', 'redCards', 'shotsOff', 'treatments', 'yellowCards', 'goalsConceded'];

export type TeamMatchH2hWidgetProps = BaseWidgetProps<IFootballTeamMatchH2hWidgetConfig>;

export const TeamMatchH2hWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: TeamMatchH2hWidgetProps) => {
  const { ref } = useRootNodeRef();
  const { oddsMarket, oddsMarketScope } = useReconcileScopeMarkets(config?.dataOddsMarket);

  const {
    showWidget,
    widgetData: { team: teams, isLoading },
    handleClick,
  } = useTeam({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: Sports.FOOTBALL,
    dataRefreshTime: config?.dataRefreshTime,
    dataTeam: config?.dataSportEntityOne.id,
    dataSeason: config?.dataSportEntityOne.seasonId,
    dataMatchId: config?.dataMatchId,
    teamToCompareWith: config?.dataSportEntityTwo,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsMarket: oddsMarket,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataWidgetCuid: cuid,
  });

  const [condensedHeader, setCondensedHeader] = useState(false);
  const measuredHeaderRef = useResizeObserver((headerElement) => {
    setCondensedHeader(headerElement.offsetWidth < LAYOUT_SIZE_CHANGE);
  });

  if (!config || !showWidget || !teams || teams.length === 0 || isLoading) {
    return isLoading ? <SMPLoader size={3} thickness={4} color='spinner' loaderMinHeight='300px' /> : null;
  }

  const firstTeam = teams.find(
    ({ team, season }) =>
      String(team.id) === String(config.dataSportEntityOne.id) && String(season.id) === String(config.dataSportEntityOne.seasonId),
  );
  const secondTeam = teams.find(
    ({ team, season }) =>
      String(team.id) === String(config.dataSportEntityTwo.id) && String(season.id) === String(config.dataSportEntityTwo.seasonId),
  );

  const normalizedTeamOneData =
    firstTeam &&
    normalizeTeamData({
      teamData: [firstTeam],
      dataEntityLinks: config.dataEntityLinks,
    });

  const normalizedTeamTwoData =
    secondTeam &&
    normalizeTeamData({
      teamData: [secondTeam],
      dataEntityLinks: config.dataEntityLinks,
    });

  if (!normalizedTeamOneData || !normalizedTeamTwoData) return null;

  const matchData = firstTeam.matchData;

  const stats: string[] = [];
  const configStats: string[] = [];

  if (Array.isArray(config.dataElements)) {
    configStats.push(...config.dataElements);
  } else if (config.dataElements && Array.isArray(config.dataElements.stats)) {
    configStats.push(...config.dataElements.stats);
  }

  if (configStats.length === 0) {
    const matchDataStatistics = Object.keys(matchData?.statistics?.[0]?.statistics ?? {});
    stats.push(...matchDataStatistics);
  } else {
    stats.push(...configStats);
  }

  if (stats.length === 0) {
    // /This is added so if only 'goals' stats is passed for example - to display only it instead of all team stats
    const filteredData =
      Array.isArray(config.dataElements) && config.dataElements.length > 0
        ? config.dataElements.filter((dataElement) => !MATCH_STATS.includes(dataElement))
        : [];

    if (filteredData.length === 0) {
      normalizedTeamOneData.statistics.forEach(({ name }) => {
        name && stats.push(name);
      });
    } else {
      stats.push(...filteredData);
    }
  }

  const handleTeamOneClick = () =>
    defaultHandleTeamClick(handleClick, { id: normalizedTeamOneData.team.id, name: normalizedTeamOneData.team.name });
  const handleTeamTwoClick = () =>
    defaultHandleTeamClick(handleClick, { id: normalizedTeamTwoData.team.id, name: normalizedTeamTwoData.team.name });

  const isFirstTeamNational = normalizedTeamOneData.team.national;
  const isSecondTeamNational = normalizedTeamTwoData.team.national;

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        css={{ background: '$sportEntityContainerPrimaryBgColor' }}
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config.dataTheme}
        isLoading={isLoading}
        refForShadow={ref}
      >
        {config.dataHeaderDisplay && (
          <Header ref={measuredHeaderRef}>
            <ProfileHead
              widgetType='team'
              team={normalizedTeamOneData.team}
              align={GenericAlign.RIGHT}
              size={GenericSize.SMALL}
              condensed={condensedHeader}
              primaryLink={{
                url: normalizedTeamOneData.team.url,
                urlTarget: normalizedTeamOneData.team.urlTarget,
                callback: handleTeamOneClick,
              }}
              displaySecondaryIcon={false}
              isMainImageRound={isFirstTeamNational}
            />
            <SMPFlex direction='column' align='center' justify='center'>
              <SMPText size='5' variant='contrast' weight={700}>
                {config.dataLabels?.vs ?? 'VS'}
              </SMPText>
            </SMPFlex>
            <ProfileHead
              widgetType='team'
              team={normalizedTeamTwoData.team}
              align={GenericAlign.LEFT}
              size={GenericSize.SMALL}
              condensed={condensedHeader}
              primaryLink={{
                url: normalizedTeamTwoData.team.url,
                urlTarget: normalizedTeamTwoData.team.urlTarget,
                callback: handleTeamTwoClick,
              }}
              displaySecondaryIcon={false}
              isMainImageRound={isSecondTeamNational}
            />
          </Header>
        )}
        {normalizedTeamOneData.tournament && normalizedTeamTwoData.tournament && (
          <SubHeader
            tournamentOne={normalizedTeamOneData.tournament}
            seasonOne={normalizedTeamOneData.season}
            tournamentTwo={normalizedTeamTwoData.tournament}
            seasonTwo={normalizedTeamTwoData.season}
            handleClick={handleClick}
          />
        )}
        <SMPStatsWrapper>
          {stats.map((stat) => {
            const camelStatName = snakeToCamelCase(stat, true);
            const snakeStatName = camelToSnakeCase(camelStatName);

            const teamOneStat = matchData?.statistics[0]?.statistics[camelStatName as keyof ITeamStatistics];
            const teamTwoStat = secondTeam?.matchData?.statistics?.[1]?.statistics[camelStatName as keyof ITeamStatistics];

            if ((teamOneStat === null || teamOneStat === undefined) && (teamTwoStat === null || teamTwoStat === undefined)) {
              return null;
            }

            const label = config?.dataLabels?.[snakeStatName] ?? toSentenceCase(snakeStatName);

            return (
              <ComparisonRow
                key={camelStatName}
                valueOne={Number(teamOneStat ?? 0)}
                valueTwo={Number(teamTwoStat ?? 0)}
                label={label}
                name={camelStatName}
                takeLowerValue={NEGATIVE_STATS.includes(camelStatName)}
              />
            );
          })}
        </SMPStatsWrapper>
        {config.dataOddsDisplay && !!matchData?.odds.length && (
          <OddsWrapper>
            <SMPText size='4' textAlign='center' weight='700' variant='contrast'>
              {config.dataLabels?.odds ?? 'Odds'}
            </SMPText>
            <OddsDisplay
              odds={matchData.odds}
              handleClick={handleClick}
              marketSelectionDisplay={config.dataOddsMarket === undefined}
              isStandalone
              oddsMarketScope={oddsMarketScope}
            />
          </OddsWrapper>
        )}
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};

const Header = styled.withConfig({ componentId: 'TeamH2hWidget', displayName: 'Header' })('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  bc: '$sportEntityContainerPrimaryBgColor',
  '& > div': { margin: 0 },
});

const OddsWrapper = styled.withConfig({ componentId: 'TeamH2hWidget', displayName: 'OddsWrapper' })('div', {
  pb: '$5',
  bc: '$sportEntityContainerPrimaryBgColor',
  '& > :first-child': { marginBottom: '$3' },
});
